import { useMemo } from 'react';

import dayjs from 'dayjs';
import { groupBy, keyBy, orderBy } from 'lodash';

import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';

import { useCityById } from '@hooks/useCurrentUserLocation';

import { useCityDragKingSeriesEntertainers } from './useCityDragKingSeriesEntertainers';
import { useCityDragKingSeriesEvents } from './useCityDragKingSeriesEvents';

export const useCityDragKingSeriesMostBookedEntertainers = (cityId: string) => {
  const { eventsAreReady, events = [] } = useCityDragKingSeriesEvents(cityId);
  const { isFetched: areSeriesEntertainersReady, entertainers: seriesEntertainers = [] } =
    useCityDragKingSeriesEntertainers(cityId);

  const castEntertainers = useMemo(() => {
    const duplicatedEvents = events.flatMap(
      (event) =>
        event.entertainers?.map(({ _id }) => ({
          ...event,
          entertainerId: _id,
        })) ?? [],
    );
    const entertainersById = keyBy(seriesEntertainers, '_id');
    const eventsGroupedByEntertainerId = groupBy(duplicatedEvents, 'entertainerId');

    return orderBy(
      Object.entries(eventsGroupedByEntertainerId)
        .filter(([entertainerId]) => entertainersById[entertainerId])
        .map(([entertainerId, events]) => {
          return { ...entertainersById[entertainerId], events };
        }),
      ({ events }) => events.length,
      'desc',
    );
  }, [seriesEntertainers, events]);

  return {
    areCastEntertainersReady: eventsAreReady && areSeriesEntertainersReady,
    castEntertainers,
    events,
  };
};
